// src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';
import { jwtDecode } from 'jwt-decode';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

const AUTH_STORAGE_KEYS = [
  'authToken',
  'user',
  'branchId',
  'channelId',
  'commerceName',
  'commerceLogo',
  'branchName',
  'config'
];

const INVALID_TOKEN_VALUES = new Set(['', 'null', 'undefined']);

const isValidStoredToken = (token) => {
  if (!token || INVALID_TOKEN_VALUES.has(token.trim())) {
    return false;
  }

  return token.split('.').length === 3;
};

const parseStoredJson = (value) => {
  if (!value || value === 'undefined') {
    return null;
  }

  try {
    return JSON.parse(value);
  } catch (error) {
    console.warn('Invalid stored auth data:', error);
    return null;
  }
};

const AuthProvider = ({ children }) => {
  const [authToken, setAuthToken] = useState(null);
  const [user, setUser] = useState(null);
  const [channelId, setChannel] = useState(null);
  const [branchId, setBranch] = useState(null);
  const [commerceName, setCommerceName] = useState(null);
  const [commerceLogo, setCommerceLogo] = useState(null);
  const [branchName, setBranchName] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  const clearSession = () => {
    setAuthToken(null);
    setUser(null);
    setChannel(null);
    setBranch(null);
    setCommerceName(null);
    setCommerceLogo(null);
    setBranchName(null);
    setConfig(null);
    AUTH_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
  };

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const storedUser = localStorage.getItem('user');
    const storedBranch = localStorage.getItem('branchId');
    const storedChannel = localStorage.getItem('channelId');
    const storedCommerce = localStorage.getItem('commerceName');
    const storedLogo = localStorage.getItem('commerceLogo');
    const storedBranchName = localStorage.getItem('branchName');
    const storedConfig = localStorage.getItem('config');

    if (!isValidStoredToken(token)) {
      clearSession();
      setLoading(false);
      return;
    }

    try {
      const decodedToken = jwtDecode(token);
      const parsedUser = parseStoredJson(storedUser);

      if (!decodedToken?.exp || decodedToken.exp * 1000 < Date.now() || !parsedUser) {
        clearSession();
        setLoading(false);
        return;
      }

      setAuthToken(token);
      setUser(parsedUser);
      setBranch(storedBranch);
      setChannel(storedChannel);
      setCommerceName(storedCommerce);
      setCommerceLogo(storedLogo);
      setBranchName(storedBranchName);
      setConfig(parseStoredJson(storedConfig));
    } catch (error) {
      console.warn('Invalid stored auth token:', error);
      clearSession();
    }

    setLoading(false);
  }, []);

  const login = (token, userData, channelId, branchId, commerceName, branchName, commerceLogo, config) => {
    setAuthToken(token);
    setUser(userData);
    setChannel(channelId);
    setBranch(branchId);
    setCommerceName(commerceName);
    setCommerceLogo(commerceLogo);
    setBranchName(branchName);
    setConfig(config);
    localStorage.setItem('authToken', token);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('branchId', branchId);
    localStorage.setItem('channelId', channelId);
    localStorage.setItem('commerceName', commerceName);
    localStorage.setItem('commerceLogo', commerceLogo);
    localStorage.setItem('branchName', branchName);
    localStorage.setItem('config', JSON.stringify(config));
  };

  const logout = async () => {
    const tokenToRevoke = authToken || localStorage.getItem('authToken');

    try {
      if (isValidStoredToken(tokenToRevoke)) {
        await api.post('/logout', {}, { headers: { Authorization: `Bearer ${tokenToRevoke}` } });
      }
    } catch (error) {
      console.error('Error during logout:', error);
    }
    clearSession();
  };  

  return (
    <AuthContext.Provider
      value={{
        authToken,
        user,
        channelId,
        branchId,
        commerceName,
        branchName,
        commerceLogo,
        config,
        setConfig,
        login,
        logout,
        loading
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
